/**
 * Authentication Context
 *
 * Manages master key authentication state
 */

import type { ReactNode } from 'react';
import { createContext, useContext, useState, useEffect } from 'react';
import { apiClient } from '../api/client';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (masterKey: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const STORAGE_KEY = 'vgmem_master_key';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for stored key on mount
  useEffect(() => {
    const storedKey = localStorage.getItem(STORAGE_KEY);
    if (storedKey) {
      validateKey(storedKey);
    } else {
      setIsLoading(false);
    }
  }, []);

  const validateKey = async (key: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      apiClient.setMasterKey(key);
      // Try to list keys to validate the master key
      await apiClient.listKeys({ limit: 1 });
      localStorage.setItem(STORAGE_KEY, key);
      setIsAuthenticated(true);
      setIsLoading(false);
      return true;
    } catch (err) {
      apiClient.clearMasterKey();
      localStorage.removeItem(STORAGE_KEY);
      setIsAuthenticated(false);
      setError(err instanceof Error ? err.message : 'Authentication failed');
      setIsLoading(false);
      return false;
    }
  };

  const login = async (masterKey: string): Promise<boolean> => {
    return validateKey(masterKey);
  };

  const logout = () => {
    apiClient.clearMasterKey();
    localStorage.removeItem(STORAGE_KEY);
    setIsAuthenticated(false);
    setError(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
